import React from 'react';
import { useTranslation } from 'react-i18next';
import { Skeleton } from '@heroui/react';
import { Widget } from '@heroui-pro/react';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';

const PLACEHOLDER_ROW_COUNT = 6;

/**
 * Mirrors `ImportHistoryView`'s row layout (8 columns, no trailing
 * action pill) so the table doesn't shift vertically when the real
 * data swaps in.
 */
export const ImportHistorySkeleton: React.FC = () => {
    const { t } = useTranslation('pages');

    const copy = React.useMemo(
        () => ({
            title: t('purchases.widgets.importHistory.title'),
        }),
        [t],
    );

    return (
        <section
            aria-busy
            aria-labelledby="import-history-widget-title"
            {...getNodeTestIdProp('import-history-section')}
        >
            <Widget
                className="flex flex-col gap-0 overflow-hidden p-0"
                {...getNodeTestIdProp('import-history-widget')}
            >
                <Widget.Header
                    className="flex items-center justify-between gap-3 border-b border-default-200 px-6 py-5"
                    {...getNodeTestIdProp('import-history-widget-header')}
                >
                    <div
                        className="flex items-center gap-3"
                        {...getNodeTestIdProp('import-history-header-titles')}
                    >
                        <Skeleton
                            className="size-8 shrink-0 rounded-lg"
                            {...getNodeTestIdProp(
                                'import-history-icon-skeleton',
                            )}
                        />
                        <div
                            className="flex flex-col gap-1.5"
                            {...getNodeTestIdProp('import-history-title-stack')}
                        >
                            <Widget.Title
                                className="text-base font-semibold leading-none text-foreground"
                                {...getNodeTestIdProp(
                                    'import-history-widget-title',
                                )}
                            >
                                {copy.title}
                            </Widget.Title>
                            <Widget.Description
                                {...getNodeTestIdProp(
                                    'import-history-widget-subtitle',
                                )}
                            >
                                <Skeleton
                                    className="h-3 w-24 rounded-md"
                                    {...getNodeTestIdProp(
                                        'import-history-widget-subtitle-skeleton',
                                    )}
                                />
                            </Widget.Description>
                        </div>
                    </div>
                </Widget.Header>
                <ul
                    className="divide-y divide-default-200"
                    {...getNodeTestIdProp('import-history-grid-skeleton')}
                >
                    {Array.from({ length: PLACEHOLDER_ROW_COUNT }).map(
                        (_, index) => (
                            <li
                                key={index}
                                className="flex items-center gap-4 px-6 py-3.5"
                                {...getNodeTestIdProp(
                                    `import-history-row-placeholder-${index}`,
                                )}
                            >
                                <Skeleton
                                    className="h-3 w-24 rounded-md"
                                    {...getNodeTestIdProp(
                                        `import-history-row-number-placeholder-${index}`,
                                    )}
                                />
                                <Skeleton
                                    className="h-3 flex-1 rounded-md"
                                    {...getNodeTestIdProp(
                                        `import-history-row-supplier-placeholder-${index}`,
                                    )}
                                />
                                <Skeleton
                                    className="h-3 w-28 rounded-md"
                                    {...getNodeTestIdProp(
                                        `import-history-row-date-placeholder-${index}`,
                                    )}
                                />
                                <Skeleton
                                    className="h-3 w-10 rounded-md"
                                    {...getNodeTestIdProp(
                                        `import-history-row-count-placeholder-${index}`,
                                    )}
                                />
                                <Skeleton
                                    className="h-3.5 w-20 rounded-md"
                                    {...getNodeTestIdProp(
                                        `import-history-row-total-placeholder-${index}`,
                                    )}
                                />
                                <Skeleton
                                    className="h-3 w-32 rounded-md"
                                    {...getNodeTestIdProp(
                                        `import-history-row-import-date-placeholder-${index}`,
                                    )}
                                />
                                <Skeleton
                                    className="h-5 w-20 rounded-full"
                                    {...getNodeTestIdProp(
                                        `import-history-row-method-placeholder-${index}`,
                                    )}
                                />
                                <Skeleton
                                    className="h-3 w-28 rounded-md"
                                    {...getNodeTestIdProp(
                                        `import-history-row-branch-placeholder-${index}`,
                                    )}
                                />
                            </li>
                        ),
                    )}
                </ul>
            </Widget>
        </section>
    );
};
